import { For, Match, Show, Switch } from "solid-js";
import { useApi, useRoles, useRoomMembers, useUsers } from "@/api";
import { Checkbox } from "@/atoms/icons";
import { useVoice } from "@/components/features/voice/context.tsx";
import { useCurrentUser } from "@/contexts/currentUser.tsx";
import { useMenu } from "@/contexts/mod.tsx";
import { useModals } from "@/contexts/modal";
import { useNavigate } from "@/contexts/router.tsx";
import { usePermissions } from "@/hooks/usePermissions.ts";
import { Item, Menu, Separator, Submenu } from "./Parts.tsx";

type UserMenuProps = {
	user_id: string;
	room_id?: string;
	channel_id?: string;
	thread_id?: string;
	admin?: boolean;
};

// the context menu for users
// TODO: hide separators when a category has no items
export function UserMenu(props: UserMenuProps) {
	const { setMenu } = useMenu();
	const api2 = useApi();
	const users2 = useUsers();
	const roomMembers2 = useRoomMembers();
	const roles2 = useRoles();
	const navigate = useNavigate();
	const currentUser = useCurrentUser();
	const user = users2.use(() => props.user_id);
	const room_member = props.room_id
		? roomMembers2.use(() => `${props.room_id!}:${props.user_id}`)
		: () => null;
	const self_id = () => currentUser()?.id;

	const { has: hasPermission, permissions } = usePermissions(
		self_id,
		() => props.room_id,
		() => props.thread_id,
	);

	const { permissions: targetPerms } = usePermissions(
		() => props.user_id,
		() => props.room_id,
		() => props.thread_id,
	);
	const canModerate = () => permissions().rank > targetPerms().rank;

	const userVoiceStates = () =>
		[...api2.voiceStates.values()].filter((s) => s.user_id === props.user_id);
	const connectedToVoice = () => userVoiceStates().length;
	const [voice, voiceActions] = useVoice();

	const sendFriendRequest = () => {
		api2.client.http.PUT("/api/v1/user/@self/friend/{target_id}", {
			params: { path: { target_id: props.user_id } },
		});
	};

	const removeFriend = () => {
		api2.client.http.DELETE("/api/v1/user/@self/friend/{target_id}", {
			params: { path: { target_id: props.user_id } },
		});
	};

	const blockUser = () => {
		api2.client.http.PUT("/api/v1/user/@self/block/{target_id}", {
			params: { path: { target_id: props.user_id } },
		});
	};

	const unblockUser = () => {
		api2.client.http.DELETE("/api/v1/user/@self/block/{target_id}", {
			params: { path: { target_id: props.user_id } },
		});
	};

	const copyUserId = () => navigator.clipboard.writeText(props.user_id);

	const logToConsole = () => console.log(JSON.parse(JSON.stringify(user())));

	const [, modalCtl] = useModals();
	const kickRoom = () => {
		if (!props.room_id) return;
		modalCtl.open({
			type: "kick",
			room_id: props.room_id,
			user_id: props.user_id,
		});
	};

	const kickThread = () => {
		api2.client.http.DELETE("/api/v1/thread/{thread_id}/member/{user_id}", {
			params: {
				path: {
					thread_id: props.thread_id!,
					user_id: props.user_id,
				},
			},
		});
	};

	const banRoom = () => {
		if (!props.room_id) return;
		modalCtl.open({
			type: "ban",
			room_id: props.room_id,
			user_id: props.user_id,
		});
	};

	const timeoutRoom = () => {
		if (!props.room_id) return;
		modalCtl.open({
			type: "timeout",
			room_id: props.room_id,
			user_id: props.user_id,
		});
	};

	const changeNickname = () => {
		if (user()?.webhook) {
			modalCtl.prompt("new name", (name) => {
				if (name === null) return;
				api2.client.http.PATCH("/api/v1/webhook/{webhook_id}", {
					params: {
						path: {
							webhook_id: props.user_id,
						},
					},
					body: {
						name: name,
					},
				});
			});
		} else {
			modalCtl.prompt("new nickname", (nick) => {
				if (nick === null) return;
				api2.client.http.PATCH("/api/v1/room/{room_id}/member/{user_id}", {
					params: {
						path: {
							room_id: props.room_id!,
							user_id: props.user_id,
						},
					},
					body: {
						override_name: nick || null,
					},
				});
			});
		}
	};

	const editIntegration = () => {
		if (props.channel_id) {
			navigate(`/channel/${props.channel_id}/settings/integrations`);
			setMenu(null);
		}
	};

	const disconnect = () => {
		api2.client.http.DELETE("/api/v1/voice/{channel_id}/member/{user_id}", {
			params: {
				path: {
					channel_id: userVoiceStates()[0].channel_id,
					user_id: props.user_id,
				},
			},
		});
	};

	const openDm = () => {
		api2.client.http.POST("/api/v1/user/@self/dm/{target_id}", {
			params: { path: { target_id: props.user_id } },
		});
	};

	const mute = () => {
		api2.client.http.PATCH("/api/v1/room/{room_id}/member/{user_id}", {
			params: { path: { room_id: props.room_id!, user_id: props.user_id } },
			body: { mute: !room_member()?.mute },
		});
	};

	const deafen = () => {
		api2.client.http.PATCH("/api/v1/room/{room_id}/member/{user_id}", {
			params: { path: { room_id: props.room_id!, user_id: props.user_id } },
			body: { deaf: !room_member()?.deaf },
		});
	};

	const suspendUser = () => {
		modalCtl.prompt("suspend reason", (reason) => {
			if (!reason) return;
			api2.client.http.POST("/api/v1/user/{user_id}/suspend", {
				params: {
					path: {
						user_id: props.user_id,
					},
				},
				headers: {
					"X-Reason": reason,
				},
				body: {},
			});
		});
	};

	const unsuspendUser = () => {
		modalCtl.prompt("unsuspend reason", (reason) => {
			if (!reason) return;
			api2.client.http.DELETE("/api/v1/user/{user_id}/suspend", {
				params: {
					path: {
						user_id: props.user_id,
					},
				},
				headers: {
					"X-Reason": reason,
				},
			});
		});
	};

	const deleteUser = () => {
		modalCtl.confirm(
			"Are you sure you want to delete this user? This action cannot be undone.",
			(confirmed) => {
				if (!confirmed) return;
				api2.client.http.DELETE("/api/v1/user/{user_id}", {
					params: {
						path: {
							user_id: props.user_id,
						},
					},
				});
			},
		);
	};

	const roles = () => roles2.listByRoom(props.room_id!);

	const RoleSubmenu = () => (
		<Submenu content="roles">
			<Show when={roles().length > 0} fallback="loading roles...">
				<For each={roles().filter((r) => r.id !== r.room_id)}>
					{(role) => (
						<Item
							onClick={(e) => {
								e.stopPropagation();
								if (room_member()?.roles.includes(role.id!)) {
									console.log("remove role");
									api2.client.http.DELETE(
										"/api/v1/room/{room_id}/role/{role_id}/member/{user_id}",
										{
											params: {
												path: {
													room_id: props.room_id!,
													role_id: role.id,
													user_id: props.user_id,
												},
											},
										},
									);
								} else {
									console.log("add role");
									api2.client.http.PUT(
										"/api/v1/room/{room_id}/role/{role_id}/member/{user_id}",
										{
											params: {
												path: {
													room_id: props.room_id!,
													role_id: role.id,
													user_id: props.user_id,
												},
											},
										},
									);
								}
							}}
						>
							<div style="display: flex; align-items: start; gap: 8px">
								<input
									type="checkbox"
									checked={room_member()?.roles.includes(role.id)}
									style="display: none;"
								/>
								<Checkbox
									checked={room_member()?.roles.includes(role.id)}
									seed={`menu-user-${props.user_id}-room-${room_member()?.room_id}-role-${role.id}`}
								/>
								<div style="margin: 2px 0">
									<div
										classList={{ has: room_member()?.roles.includes(role.id) }}
									>
										{role.name}
									</div>
									<div class="dim">{role.description}</div>
								</div>
							</div>
						</Item>
					)}
				</For>
				<Show when={roles().filter((r) => r.id !== r.room_id).length === 0}>
					<div>no roles</div>
				</Show>
			</Show>
		</Submenu>
	);

	const hasRoles = () => roles().filter((r) => r.id !== r.room_id).length > 0;
	const _hasAnyPermission = (checks: Array<() => boolean>) =>
		checks.some((c) => c());

	return (
		<Menu>
			<Switch>
				<Match when={user()?.webhook}>
					<Item onClick={changeNickname}>change name</Item>
					<Show when={props.channel_id}>
						<Item onClick={editIntegration}>edit integration</Item>
					</Show>
				</Match>
				<Match when={!user()?.webhook}>
					<Show when={props.thread_id}>
						<Item>mention</Item>
					</Show>
					<Show when={user()?.relationship?.relation !== "Block"}>
						<Item onClick={openDm}>dm</Item>
					</Show>
					<Item
						onClick={() =>
							user()?.relationship?.relation === "Block"
								? unblockUser()
								: blockUser()
						}
					>
						{user()?.relationship?.relation === "Block" ? "unblock" : "block"}
					</Item>
					<Show when={false}>
						<Item>(un)ignore</Item>
					</Show>
					<Switch>
						<Match when={user()?.relationship?.relation === null}>
							<Item onClick={sendFriendRequest}>add friend</Item>
						</Match>
						<Match when={user()?.relationship?.relation === "Friend"}>
							<Item onClick={removeFriend}>remove friend</Item>
						</Match>
						<Match when={user()?.relationship?.relation === "Incoming"}>
							<Item onClick={sendFriendRequest}>accept friend request</Item>
						</Match>
						<Match when={user()?.relationship?.relation === "Outgoing"}>
							<Item onClick={removeFriend}>cancel friend request</Item>
						</Match>
					</Switch>
					<Separator />
					<Show
						when={
							hasPermission("MemberNicknameManage") ||
							(hasPermission("MemberNickname") && props.user_id === self_id())
						}
					>
						<Item onClick={changeNickname}>change nickname</Item>
					</Show>
					<Show when={hasPermission("MemberKick") && canModerate()}>
						<Item onClick={kickRoom} color="danger">
							kick
						</Item>
					</Show>
					<Show when={hasPermission("MemberBan") && canModerate()}>
						<Item onClick={banRoom} color="danger">
							ban
						</Item>
					</Show>
					<Show when={hasPermission("MemberTimeout") && canModerate()}>
						<Item onClick={timeoutRoom} color="danger">
							timeout
						</Item>
					</Show>
					<Show when={hasPermission("MemberKick") && props.thread_id}>
						<Item onClick={kickThread} color="danger">
							remove from thread
						</Item>
					</Show>
					<Show
						when={hasPermission("RoleApply") && props.room_id && hasRoles()}
					>
						<RoleSubmenu />
					</Show>
					<Separator />
					<Show when={props.user_id !== self_id() && connectedToVoice()}>
						<li>
							<label style="display:block;padding:0 8px;padding-top:8px">
								<div class="dim">volume</div>
								<input
									type="range"
									min="0"
									max="1"
									step="any"
									// list="volume-detents"
									value={voice.preferences.get(props.user_id)?.volume ?? 1}
									onInput={(e) =>
										voice.preferences.set(props.user_id, {
											...(voice.preferences.get(props.user_id) ?? {
												mute: false,
												mute_video: false,
												volume: 1,
											}),
											volume: parseFloat(e.target.value),
										})
									}
								/>
							</label>
						</li>
						<Item
							onClick={() => {
								const c = voice.preferences.get(props.user_id) ?? {
									mute: false,
									mute_video: false,
									volume: 1,
								};
								c.mute = !c.mute;
								voice.preferences.set(props.user_id, { ...c });
							}}
						>
							{voice.preferences.get(props.user_id)?.mute === true
								? "unmute"
								: "mute"}
						</Item>
					</Show>
					<Show when={props.user_id === self_id() && connectedToVoice()}>
						<Item onClick={() => voiceActions.toggleMicrophone()}>
							{voice.muted ? "unmute" : "mute"}
						</Item>
						<Item onClick={() => voiceActions.toggleDeafened()}>
							{voice.deafened ? "undeafen" : "deafen"}
						</Item>
					</Show>
					<Show when={hasPermission("VoiceMute")}>
						<Item onClick={mute} color="danger">
							{room_member()?.mute ? "room unmute" : "room mute"}
						</Item>
					</Show>
					<Show when={hasPermission("VoiceDeafen")}>
						<Item onClick={deafen} color="danger">
							{room_member()?.deaf ? "room undeafen" : "room deafen"}
						</Item>
					</Show>
					<Show
						when={hasPermission("VoiceDisconnect" as any) && connectedToVoice()}
					>
						<Item onClick={disconnect} color="danger">
							disconnect
						</Item>
					</Show>
					<Show when={hasPermission("VoiceMove") && connectedToVoice()}>
						<Item>move to</Item>
					</Show>
					<Show when={props.admin}>
						<Show when={user()?.relationship?.relation !== "Block"}>
							<Item onClick={openDm}>dm</Item>
						</Show>
						<Item
							onClick={() =>
								user()?.relationship?.relation === "Block"
									? unblockUser()
									: blockUser()
							}
						>
							{user()?.relationship?.relation === "Block" ? "unblock" : "block"}
						</Item>
						<Show when={false}>
							<Item>(un)ignore</Item>
						</Show>
						<Show when={hasRoles()}>
							<Separator />
							<RoleSubmenu />
						</Show>
						<Separator />
						<Show when={user()?.suspended}>
							<Item onClick={unsuspendUser}>unsuspend user</Item>
						</Show>
						<Show when={!user()?.suspended}>
							<Item onClick={suspendUser}>suspend user</Item>
						</Show>
						<Item onClick={deleteUser} color="danger">
							delete user
						</Item>
					</Show>
				</Match>
			</Switch>
			<Separator />
			<Item onClick={copyUserId}>copy user id</Item>
			<Item onClick={logToConsole}>log to console</Item>
		</Menu>
	);
}
